import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AuthPage from "@/components/AuthPage";
import TaskInput from "@/components/TaskInput";
import PriorityMatrix from "@/components/PriorityMatrix";
import PrioritizedList from "@/components/PrioritizedList";
import EnergyToggle from "@/components/EnergyToggle";
import TemplateManager from "@/components/TemplateManager";
import TaskBubbleDragOverlay from "@/components/TaskBubbleDragOverlay";
import InboxBubbles from "@/components/InboxBubbles";
import TaskEditDialog from "@/components/TaskEditDialog";
import BoardControls from "@/components/BoardControls";
import TaskLog from "@/components/TaskLog";
import ReportQuickView from "@/components/ReportQuickView";
import ReportsPage from "@/components/ReportsPage";
import OnboardingTour, { STORAGE_KEY as ONBOARDING_KEY } from "@/components/OnboardingTour";
import { useTaskManager } from "@/hooks/useTaskManager";
import { getPrioritizedTasks } from "@/hooks/usePrioritization";
import { LogOut, HelpCircle, ScrollText, BarChart3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VALID_QUADRANTS = [
  "hi-impact-lo-effort", "hi-impact-med-effort", "hi-impact-hi-effort",
  "med-impact-lo-effort", "med-impact-med-effort", "med-impact-hi-effort",
  "lo-impact-lo-effort", "lo-impact-med-effort", "lo-impact-hi-effort",
];

function Dashboard() {
  const { logout } = useAuth();
  const tm = useTaskManager();
  const [energyLevel, setEnergyLevel] = useState("high");
  const [activeId, setActiveId] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const [showTaskLog, setShowTaskLog] = useState(false);
  const [showReportQuick, setShowReportQuick] = useState(false);
  const [showReportsPage, setShowReportsPage] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => { tm.fetchData(); }, [tm.fetchData]);

  // Show onboarding tour on first visit
  useEffect(() => {
    if (!tm.loading && !localStorage.getItem(ONBOARDING_KEY)) {
      const timer = setTimeout(() => setShowTour(true), 600);
      return () => clearTimeout(timer);
    }
  }, [tm.loading]);

  // Weekly summary toast on first login of the week
  useEffect(() => {
    if (tm.loading) return;
    const getWeekKey = () => {
      const d = new Date();
      const day = d.getDay();
      const mon = new Date(d);
      mon.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
      return `prism_weekly_${mon.toISOString().split("T")[0]}`;
    };
    const weekKey = getWeekKey();
    if (localStorage.getItem(weekKey)) return;

    const timer = setTimeout(async () => {
      try {
        const now = new Date();
        const day = now.getDay();
        const lastMon = new Date(now);
        lastMon.setDate(now.getDate() - day + (day === 0 ? -6 : 1) - 7);
        const lastSun = new Date(lastMon);
        lastSun.setDate(lastMon.getDate() + 6);
        const res = await axios.get(`${API}/reports/summary`, {
          params: {
            start_date: lastMon.toISOString().split("T")[0],
            end_date: lastSun.toISOString().split("T")[0],
          },
        });
        const d = res.data;
        if (d.tasks_completed > 0 || d.tasks_created > 0) {
          const pct = Math.round(d.completion_rate * 100);
          toast(`Last week: ${d.tasks_completed} tasks done (${pct}% rate)`, {
            description: `${d.tasks_created} created — Click Report for details`,
            duration: 8000,
          });
        }
        localStorage.setItem(weekKey, "1");
      } catch (err) {
        console.error("Weekly summary fetch failed:", err);
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [tm.loading]);

  // DnD handlers
  const handleDragStart = (event) => setActiveId(event.active.id);
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;
    const dropTarget = over.id;
    if (dropTarget === "inbox") {
      tm.updateQuadrant(active.id, null);
    } else if (VALID_QUADRANTS.includes(dropTarget)) {
      tm.updateQuadrant(active.id, dropTarget);
    }
  };

  // Derived data
  const inboxTasks = tm.tasks.filter((t) => !t.quadrant && !t.completed);
  const matrixTasks = tm.tasks.filter((t) => t.quadrant && !t.completed);
  const activeTask = tm.tasks.find((t) => t.id === activeId);
  const completedTasks = tm.tasks.filter((t) => t.completed);
  const prioritizedTasks = getPrioritizedTasks(matrixTasks, energyLevel);

  if (tm.loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading your workspace...</p>
      </div>
    );
  }

  if (showReportsPage) {
    return (
      <>
        <ReportsPage onBack={() => setShowReportsPage(false)} />
        <Toaster position="bottom-right" richColors />
      </>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <Helmet><title>Dashboard - Prism Planner</title></Helmet>
      <div className="app-container">
        <header className="app-header">
          <div className="header-left">
            <h1 className="app-title">Prism Planner</h1>
            <span className="app-subtitle">Drag. Prioritize. Execute.</span>
          </div>
          <div className="header-right">
            <BoardControls
              completedCount={completedTasks.length}
              onClearCompleted={tm.clearCompleted}
              onResetBoard={tm.resetBoard}
              onDataChanged={tm.fetchData}
            />
            <EnergyToggle value={energyLevel} onChange={setEnergyLevel} />
            <button className="board-ctrl-btn" onClick={() => setShowTaskLog(true)} title="View task log" aria-label="Task log" data-testid="task-log-btn">
              <ScrollText size={13} /><span>Log</span>
            </button>
            <button className="board-ctrl-btn" onClick={() => setShowReportQuick(true)} title="Weekly report" aria-label="Weekly report" data-testid="report-btn">
              <BarChart3 size={13} /><span>Report</span>
            </button>
            <button className="logout-btn" onClick={logout} title="Sign out" aria-label="Sign out" data-testid="logout-btn">
              <LogOut size={15} />
            </button>
            <button className="tour-restart-btn" onClick={() => { localStorage.removeItem(ONBOARDING_KEY); setShowTour(true); }} title="Replay tour" aria-label="Replay onboarding tour" data-testid="tour-restart-btn">
              <HelpCircle size={15} />
            </button>
          </div>
        </header>

        <main className="main-layout">
          <div className="capture-column">
            <div className="section-card">
              <div className="section-header">
                <h2 className="section-title">Capture</h2>
                <span className="section-hint">Type a task and press Enter</span>
              </div>
              <TaskInput onCreateTask={tm.createTask} />
              <TemplateManager templates={tm.templates} onInstantiate={tm.instantiateTemplate} onDelete={tm.deleteTemplate} onCreate={tm.createTemplate} />
              <InboxBubbles tasks={inboxTasks} onEdit={tm.setEditingTask} onDelete={tm.deleteTask} onSaveAsTemplate={tm.saveAsTemplate} onMoveToQuadrant={tm.updateQuadrant} onToggleUrgent={tm.toggleUrgent} />
            </div>
          </div>

          <div className="matrix-column">
            <div className="section-card matrix-card">
              <div className="section-header">
                <h2 className="section-title">Priority Matrix</h2>
                <span className="section-hint">Drop tasks into quadrants</span>
              </div>
              <PriorityMatrix tasks={matrixTasks} onEdit={tm.setEditingTask} onDelete={tm.deleteTask} onMoveToQuadrant={tm.updateQuadrant} onToggleUrgent={tm.toggleUrgent} />
            </div>
          </div>

          <div className="list-column">
            <div className="section-card">
              <div className="section-header">
                <h2 className="section-title">Next Up</h2>
                <span className="section-hint" data-testid="auto-priority-hint">Auto-sorted by {energyLevel} energy</span>
              </div>
              <PrioritizedList tasks={prioritizedTasks} completedTasks={completedTasks} onToggle={tm.toggleTask} onEdit={tm.setEditingTask} onDelete={tm.deleteTask} onToggleSubtask={tm.toggleSubtask} energyLevel={energyLevel} />
            </div>
          </div>
        </main>
      </div>

      <DragOverlay dropAnimation={null} style={{ width: 'auto', height: 'auto' }}>
        {activeTask ? <TaskBubbleDragOverlay task={activeTask} /> : null}
      </DragOverlay>

      {tm.editingTask && (
        <TaskEditDialog
          task={tm.editingTask}
          onClose={() => tm.setEditingTask(null)}
          onSave={(updates) => { tm.updateTask(tm.editingTask.id, updates); tm.setEditingTask(null); }}
          onDelete={() => { tm.deleteTask(tm.editingTask.id); tm.setEditingTask(null); }}
          onAddSubtask={tm.addSubtask}
          onToggleSubtask={tm.toggleSubtask}
          onDeleteSubtask={tm.deleteSubtask}
          onToggleUrgent={tm.toggleUrgent}
        />
      )}

      <OnboardingTour active={showTour} onComplete={() => setShowTour(false)} />
      <TaskLog open={showTaskLog} onClose={() => setShowTaskLog(false)} />
      <ReportQuickView open={showReportQuick} onClose={() => setShowReportQuick(false)} onViewFull={() => { setShowReportQuick(false); setShowReportsPage(true); }} />
      <Toaster position="bottom-right" richColors />
    </DndContext>
  );
}

function AppContent() {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="loading-spinner" /><p>Loading...</p></div>;
  if (!user) return <AuthPage />;
  return <Dashboard />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
